<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { Ref } from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import type { OpenShiftForm } from '~/types/shift'

interface DialogData {
  businessId?: string
}

interface DialogRef {
  close: () => void
  data?: DialogData
}

const { t } = useNuxtApp().$i18n
const dialogRef = inject<Ref<DialogRef>>('dialogRef')
const user = useSupabaseUser()

// Loading state
const loading = ref(true)
const initError = ref<string | null>(null)

// Access data through dialogRef.value.data (PrimeVue pattern)
const dialogBusinessId = computed(() => dialogRef?.value?.data?.businessId)
const routeBusinessId = useRouteParam('buuid') as string
const businessId = dialogBusinessId.value || routeBusinessId

// Store (will be initialized in onMounted)
let shiftStore: ReturnType<typeof useShiftStore>

// Initialize store on mount
onMounted(async () => {
  try {
    // Validate businessId before initializing store
    if (!businessId) {
      throw new Error('Business ID is required to open shift dialog')
    }

    // Initialize store with businessId from dialog data or route
    shiftStore = await createShiftStore(businessId)
  }
  catch (error) {
    const errorMessage = error instanceof Error ? error.message : t('Failed to initialize shift form')
    initError.value = errorMessage
    useErrorToast(errorMessage)
  }
  finally {
    loading.value = false
  }
})

async function submitWrapper(formData: OpenShiftForm) {
  if (!formData || !businessId || !shiftStore) {
    useErrorToast('Invalid form data')
    return
  }

  try {
    const success = await shiftStore.createShift(formData)
    if (success) {
      useSuccessToast('Shift opened successfully')
      dialogRef?.value?.close()
    }
    // Error handling is done in the store, no need for additional error toast here
  }
  catch (error) {
    const errorMessage = error instanceof Error ? error.message : t('Open shift failed')
    useErrorToast(errorMessage)
  }
}
</script>

<template>
  <!-- Loading State -->
  
<FormKitLazyProvider config-file="true">
<div
    v-if="loading"
    class="flex justify-center items-center p-8"
  >
    <div class="text-center">
      <ProgressSpinner size="50px" />
      <p class="mt-4 text-gray-600">
        {{ t('Loading shift data') }}...
      </p>
    </div>
  </div>

  <!-- Error State -->
  <div
    v-else-if="initError"
    class="flex justify-center items-center p-8"
  >
    <div class="text-center">
      <i class="pi pi-exclamation-triangle text-red-500 text-4xl mb-4" />
      <p class="text-red-600">
        {{ initError }}
      </p>
    </div>
  </div>

  <!-- Form Content -->
  <FormKit
    v-else
    id="order-details"
    type="form"
    :actions="false"
    @submit="submitWrapper"
  >
    <p class="text-sm text-surface-600 mb-3">
      {{ t('User') }}: <span class="font-medium text-surface-800">{{ user?.email }}</span>
    </p>
    <FormKit
      type="hidden"
      name="user_id"
      :value="user?.id"
    />
    <FormKit
      type="text"
      :label="t('Opening cash amount')"
      placeholder="0.00"
      name="opening_cash_amount"
      validation="number"
    />
    <FormKit
      type="textarea"
      :label="t('Notes')"
      name="notes"
    />
    <FormKit
      type="submit"
      :label="t('Open shift')"
    />
  </FormKit>
</FormKitLazyProvider>

</template>
